/**
 * @FIXME(shawk):
 * Until an upstream dependency issue in "eslint-plugin-react" is resolved,
 * disabling eslint rule "react/prop-types" is the recommended approach
 * to resolve false positives in eslint.
 *
 * see:
 * - https://github.com/yannickcr/eslint-plugin-react/issues/3140
 * - https://github.com/redwoodjs/redwood/pull/3762
 */
 
import React from 'react';
import cx from 'classnames';
import { TFunction } from 'i18next';
import { FieldLabel, HelperText } from 'components/Forms';
import { RadioCardInput } from 'components/Forms/inputs/RadioCardInput';
import { ConnectedFormInputProps } from 'types/form-field';
import type { Option } from 'components/Forms';
import { RadioInputDot } from '../RadioCardInput/RadioInputDot';

export type Props = {
  /**
   * Radio button options to display
   */
  options: Option[];

  /**
   * Disables the display of error messages in this component. Should be used only
   * if the error is otherwise displayed.
   */
  suppressError?: boolean;

  /**
   * Show a filled circle next to the selected option
   */
  showSelectedDot?: boolean;

  tPrefixKey?: string;
  tFunc?: TFunction;
} & ConnectedFormInputProps<HTMLInputElement>;

export const StackedRadioCardGroup = React.forwardRef<HTMLInputElement, Props>(function StackedRadioCardGroupWithRef(
  {
    name,
    label,
    error,
    hint,
    disabled = false,
    options,
    suppressError = false,
    showSelectedDot = false,
    className,
    labelClassName,
    setError,
    setValue,
    clearValue,
    autoFocus,
    ...inputProps
  },
  ref?: React.ForwardedRef<HTMLInputElement>,
): JSX.Element {
  return (
    <fieldset className={className}>
      <FieldLabel as="legend" className={cx(labelClassName, 'mb-16')}>
        {label}
      </FieldLabel>

      <div className="grid gap-16">
        {options.map((option, index) => {
          const id = `${name}-${option.value}`;

          return (
            <RadioCardInput
              {...inputProps}  
              autoFocus={autoFocus && index === 0}
              ref={ref}
              id={id}
              key={id}
              align="left"
              defaultChecked={option.value === inputProps.value}
              name={name}
              hasError={!!error}
              disabled={disabled || option.isDisabled === true}
              highlightSelected={!showSelectedDot}
              option={
                showSelectedDot ? { ...option, startAdornment: checked => <RadioInputDot checked={checked} /> } : option
              }
            />
          );
        })}
      </div>

      {error && (
        <HelperText error className={cx({ 'sr-only': suppressError })}>
          {error}
        </HelperText>
      )}

      {hint && <HelperText>{hint}</HelperText>}
    </fieldset>
  );
});
